import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { v4 as uuidv4 } from "uuid"

import type { DisplayMode, NotificationItem, NotificationKind } from "./types"

const MAX_NOTIFICATIONS = 50
const AUTO_CLOSE_DELAY = 10_000

export function useErrorNotificationState() {
	const [notifications, setNotifications] = useState<NotificationItem[]>([])
	const [hasInteracted, setHasInteracted] = useState(false)
	const [isExpanded, setIsExpanded] = useState(false)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Ref to allow addError to check hasInteracted without depending on it
	const hasInteractedRef = useRef(false)
	useEffect(() => {
		hasInteractedRef.current = hasInteracted
	}, [hasInteracted])

	const cancelAutoCloseTimer = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current)
			timerRef.current = null
		}
	}, [])

	const clearAll = useCallback(() => {
		setNotifications([])
		setHasInteracted(false)
		hasInteractedRef.current = false
		setIsExpanded(false)
		cancelAutoCloseTimer()
	}, [cancelAutoCloseTimer])

	// Ref for clearAll so timer callback always calls latest version
	const clearAllRef = useRef(clearAll)
	useEffect(() => {
		clearAllRef.current = clearAll
	}, [clearAll])

	const addNotification = useCallback(
		(message: string, kind: NotificationKind) => {
			const newNotification: NotificationItem = {
				id: uuidv4(),
				message,
				timestamp: Date.now(),
				kind,
			}
			setNotifications((prev) => {
				const updated = [...prev, newNotification]
				return updated.length > MAX_NOTIFICATIONS
					? updated.slice(updated.length - MAX_NOTIFICATIONS)
					: updated
			})
			// Start/restart auto-close timer if user hasn't interacted
			if (!hasInteractedRef.current) {
				if (timerRef.current) {
					clearTimeout(timerRef.current)
					timerRef.current = null
				}
				timerRef.current = setTimeout(() => {
					clearAllRef.current()
				}, AUTO_CLOSE_DELAY)
			}
		},
		[]
	)

	// addError: stable reference (empty deps) for ApolloClient re-creation prevention
	const addError = useCallback(
		(message: string) => {
			addNotification(message, "error")
		},
		[addNotification]
	)

	const addInfo = useCallback(
		(message: string) => {
			addNotification(message, "info")
		},
		[addNotification]
	)

	const dismissNotification = useCallback((id: string) => {
		setNotifications((prev) => prev.filter((e) => e.id !== id))
	}, [])

	const handleBannerClick = useCallback(() => {
		setHasInteracted(true)
		hasInteractedRef.current = true
		cancelAutoCloseTimer()
	}, [cancelAutoCloseTimer])

	const handleStackedClick = useCallback(() => {
		handleBannerClick()
		setIsExpanded(true)
	}, [handleBannerClick])

	// Reset expanded/interacted state when all notifications are dismissed individually
	useEffect(() => {
		if (notifications.length === 0) {
			setIsExpanded(false)
			setHasInteracted(false)
			hasInteractedRef.current = false
			cancelAutoCloseTimer()
		}
	}, [notifications.length, cancelAutoCloseTimer])

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current)
			}
		}
	}, [])

	const displayMode: DisplayMode = useMemo(() => {
		if (notifications.length === 0) return "hidden"
		if (isExpanded) return "expanded"
		if (notifications.length === 1) return "single"
		return "stacked"
	}, [notifications.length, isExpanded])

	return {
		notifications,
		displayMode,
		addError,
		addInfo,
		dismissError: dismissNotification,
		clearAll,
		handleBannerClick,
		handleStackedClick,
	}
}
